import React, { useMemo, useState, useCallback } from "react";
import { message } from "antd";
import { EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import type { ButtonConfig } from "@ms/shared-components";
import { CompsTable } from "@ms/shared-components";
import { Space, SpaceType } from "../../../types/space";
import type { CompsTreeTreeNodeData as TreeNodeData } from "@ms/shared-components";
import {
  createDetailTabs,
  basicInfoColumns,
  createBasicInfoData,
  coordinateColumns,
  createCoordinateData,
  drawingsColumns,
  createDrawingsData,
} from "../configs/detailConfig";

interface UseSpaceDetailPanelProps {
  selectedSpace: Space | null;
  treeData: TreeNodeData[];
  setEditingSpace: (space: Space | null) => void;
  setFormModalVisible: (visible: boolean) => void;
  handleDetailPanelDelete: (space: Space) => void;
}

export const useSpaceDetailPanel = ({
  selectedSpace,
  treeData,
  setEditingSpace,
  setFormModalVisible,
  handleDetailPanelDelete,
}: UseSpaceDetailPanelProps) => {
  // 当前激活的 tab
  const [activeTabKey, setActiveTabKey] = useState<string>("basic");

  // 查找父级空间的函数
  const findParentSpace = useCallback(
    (nodes: TreeNodeData[], targetParentId: string): Space | null => {
      for (const node of nodes) {
        if (node.key === targetParentId) return node.space as Space;
        if (node.children) {
          const found = findParentSpace(node.children, targetParentId);
          if (found) return found;
        }
      }
      return null;
    },
    []
  );

  // 处理添加子空间
  const handleAddChild = useCallback(
    (space: Space) => {
      if (space.type === SpaceType.SPACE) {
        message.warning("空间类型的节点不能添加子空间");
        return;
      }
      // 为添加子空间创建一个新的空对象，只包含必要的父级信息
      setEditingSpace({
        parentId: space.id,
        parentName: space.name,
        type: space.type, // 临时保存父级类型，用于确定子空间类型
      } as any);
      setFormModalVisible(true);
    },
    [setEditingSpace, setFormModalVisible]
  );

  // 处理编辑按钮点击
  const handleEdit = useCallback(() => {
    if (!selectedSpace) return;

    // 如果节点来自酒店，不允许编辑
    if (selectedSpace.fromHotel === true) {
      message.warning('来自酒店的节点不允许编辑');
      return;
    }

    // 编辑时需要找到父级空间的名称
    let editSpace = { ...selectedSpace };
    if (selectedSpace.parentId) {
      const parentSpace = findParentSpace(treeData, selectedSpace.parentId);
      if (parentSpace) {
        (editSpace as any).parentName = parentSpace.name;
      }
    }
    setEditingSpace(editSpace);
    setFormModalVisible(true);
  }, [
    selectedSpace,
    treeData,
    findParentSpace,
    setEditingSpace,
    setFormModalVisible,
  ]);

  // 创建详情面板的按钮配置
  const detailButtons = useMemo<ButtonConfig[]>(() => {
    if (!selectedSpace) return [];

    // 如果节点来自酒店，不显示编辑和删除按钮
    const isFromHotel = selectedSpace.fromHotel === true;
    
    const buttons: ButtonConfig[] = [];
    
    // 如果不是来自酒店，才显示删除和编辑按钮
    if (!isFromHotel) {
      buttons.push(
        {
          key: "delete",
          text: "删除",
          icon: <DeleteOutlined />,
          type: "default",
          danger: true,
          onClick: () => handleDetailPanelDelete(selectedSpace),
          group: "primary",
        },
        {
          key: "edit",
          text: "编辑",
          icon: <EditOutlined />,
          type: "default",
          onClick: handleEdit,
          group: "primary",
        }
      );
    }
    
    // 新建子空间按钮始终显示（但会根据类型禁用）
    buttons.push({
      key: "addChild",
      text: "新建子空间",
      icon: <PlusOutlined />,
      type: "primary",
      disabled: selectedSpace.type === SpaceType.SPACE,
      onClick: () => handleAddChild(selectedSpace),
      group: "primary",
    });
    
    return buttons;
  }, [selectedSpace, handleDetailPanelDelete, handleEdit, handleAddChild]);

  // 创建 tabs 配置
  const detailTabs = useMemo(
    () => createDetailTabs(selectedSpace),
    [selectedSpace]
  );

  // 基本信息表格数据
  const basicInfoData = useMemo(
    () => createBasicInfoData(selectedSpace),
    [selectedSpace]
  );

  // 坐标信息表格数据
  const coordinateData = useMemo(
    () => createCoordinateData(selectedSpace),
    [selectedSpace]
  );

  // 图纸模型表格数据
  const drawingsData = useMemo(
    () => createDrawingsData(selectedSpace),
    [selectedSpace]
  );

  // 根据 activeTabKey 渲染对应的表格内容
  const renderTabContent = useCallback(() => {
    if (!selectedSpace) return null;

    switch (activeTabKey) {
      case "basic":
        return (
          <CompsTable
            tableProps={{
              columns: basicInfoColumns,
              dataSource: basicInfoData,
              pagination: false,
            }}
            showColumnSetting={false}
            autoScroll={true}
          />
        );
      case "coordinate":
        return (
          <CompsTable
            tableProps={{
              columns: coordinateColumns,
              dataSource: coordinateData,
              pagination: false,
            }}
            showColumnSetting={true}
            storageKey="space_coordinate_table"
            autoScroll={true}
          />
        );
      case "drawings":
        return (
          <CompsTable
            tableProps={{
              columns: drawingsColumns,
              dataSource: drawingsData,
              pagination: false,
            }}
            showColumnSetting={true}
            storageKey="space_drawings_table"
            autoScroll={true}
          />
        );
      default:
        return null;
    }
  }, [
    activeTabKey,
    selectedSpace,
    basicInfoData,
    coordinateData,
    drawingsData,
  ]);

  return {
    activeTabKey,
    setActiveTabKey,
    detailButtons,
    detailTabs,
    renderTabContent,
    handleAddChild,
    handleEdit,
  };
};
